import { AxiosInstance } from "axios";
import {
  getData,
  getUserToken,
  removeData,
  saveData,
} from "../utils/localStorage";
import GenericApi from "./genericApi";
import { AuthResponse } from "@/types/Auth";
import { authStorageUser } from "@/config/constants/StorageKeys";
import { ApiRoutes } from "@/config/routes/ApiRoutes";
import { Jwt } from "@/utils/JWT";

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];
let tries = 0;
const maxTries = 3;

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

let refreshPromise: Promise<string | null> | null = null;

const refreshAccessToken = async (): Promise<string | null> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const loginData = getData<AuthResponse>(authStorageUser);
      if (!loginData?.refresh) return null;

      const response = await GenericApi.post<AuthResponse>(
        ApiRoutes.REFRESH_TOKEN,
        { refresh: loginData.refresh }
      );

      saveData(authStorageUser, {
        ...response.data,
        user: loginData.user,
      });

      return response?.data?.access ?? null;
    } catch {
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};


export const setupInterceptors = (axiosInstance: AxiosInstance) => {
  axiosInstance.interceptors.request.use(
    async (config) => {
      let token = getUserToken();

      if (token) {
        const jwt = new Jwt(token);


        if (jwt.isExpired()) {
          const newToken = await refreshAccessToken();

          if (!newToken) {
            redirectLogin();
            return Promise.reject("Session expired");
          }

          token = newToken;
        }

        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    }
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        window.location.pathname !== "/login"
      ) {
        if (tries >= maxTries) {
          redirectLogin();
          return Promise.reject(error);
        }

        if (!isRefreshing) {
          tries++;
          isRefreshing = true;

          try {
            const loginData = getData<AuthResponse>(authStorageUser);
            const loginResponse = await GenericApi.post<AuthResponse>(
              ApiRoutes.REFRESH_TOKEN,
              {
                refresh: loginData?.refresh,
              }
            );

            saveData(authStorageUser, {
              ...loginResponse.data,
              user: loginData?.user,
            });

            if (loginResponse.data?.access) {
              onRefreshed(loginResponse.data?.access);
            } else {
              redirectLogin();
            }

            isRefreshing = false;
            tries = 0;

            return axiosInstance(originalRequest);
          } catch (refreshError) {
            console.error("Failed to refresh token", refreshError);
            redirectLogin();
            return Promise.reject(refreshError);
          }
        }
      }

      return Promise.reject(error);
    }
  );
};

const redirectLogin = () => {
  window.location.href = "/login";
  removeData(authStorageUser);
  tries = 0;
};
